/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */
import moment from 'moment';

import React, { Fragment } from 'react';
import TRP from 'components/shared/TRP/TRP';
import Store from 'state/Store';

import { selectStatementsTimePeriod } from 'state/statements/StatementsActions';

import {
  ALL_DOCUMENT_TYPES,
  TAX_FORM_DOC_SELECTION,
  MUTUAL_FUNDS,
  BROKERAGE,
  ACTIVE_PLUS,
  ACTIVE_ADVISORY_SERVICE,
  COLLEGE_SAVINGS,
  CONFIRMATION_DOC_SELECTION,
  STATEMENTS_DOC_SELECTION
} from 'state/statements/StatementsSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { statementPageNoTaxDocumentEnabled } from 'services/FeatureFlags';

export const currentYear = moment().year().toString();
export const priorYear = moment().subtract(1, 'years').year().toString();

export const BROKERAGE_CONFIRMATION = 40;

export const mapStatementsProductName = (ownedProducts) => {
  const ownedProduct = ownedProducts[0];
  if (
    ownedProduct === 'maryland529' ||
    ownedProduct === 'national529' ||
    ownedProduct === 'alaska529'
  ) {
    return COLLEGE_SAVINGS;
  }
  return ownedProduct;
};

const handleClick = () => {
  Store.dispatch(selectStatementsTimePeriod(priorYear.toString()));
};

export const shouldFetchDocuments = (
  accountsStatus,
  fetchStatus,
  docStatus,
  isTaxPageEntry = false
) => {
  return (
    (accountsStatus === REQUEST_STATES.received &&
      !fetchStatus &&
      docStatus !== REQUEST_STATES.received) ||
    isTaxPageEntry
  );
};

export const statementsNotAvailableText = (
  documentTypes,
  timePeriod,
  productTypes,
  hasAlaska529Only
) => {
  if (
    documentTypes.selection === ALL_DOCUMENT_TYPES &&
    timePeriod.selection === currentYear
  ) {
    if (productTypes.selection) {
      return (
        <Fragment>
          There are no documents available for the selected criteria. Your{' '}
          {priorYear} documents are available{' '}
          <button
            className="text-link"
            onClick={handleClick}>
            here
          </button>
        </Fragment>
      );
    }
  }

  if (
    documentTypes.selection === STATEMENTS_DOC_SELECTION &&
    timePeriod.selection === currentYear
  ) {
    if (productTypes.selection) {
      return (
        <Fragment>
          There are no documents available for the selected criteria. Your{' '}
          {priorYear} statements are available{' '}
          <button
            className="text-link"
            onClick={handleClick}>
            here
          </button>
        </Fragment>
      );
    }
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === currentYear
  ) {
    if (productTypes.selection) {
      return (
        <Fragment>
          There are no documents available for the selected criteria. Your{' '}
          {priorYear} tax forms are available{' '}
          <button
            className="text-link"
            onClick={handleClick}>
            here
          </button>
        </Fragment>
      );
    }
  }

  if (
    documentTypes.selection === CONFIRMATION_DOC_SELECTION &&
    timePeriod.selection === currentYear
  ) {
    if (productTypes.selection) {
      return (
        <Fragment>
          There are no documents available for the selected criteria. Your{' '}
          {priorYear} confirmations are available{' '}
          <button className="text-link" onClick={handleClick}>
            here
          </button>
        </Fragment>
      );
    }
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === ALL_DOCUMENT_TYPES
  ) {
    return (
      <Fragment>Choose a specific product type to find tax forms.</Fragment>
    );
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === MUTUAL_FUNDS
  ) {
    return (
      <Fragment>
        <div className="no-statement-title">
          There are no Mutual Fund tax forms available for the selected time
          period.
        </div>
        {statementPageNoTaxDocumentEnabled() ?
          (
            <>
              <div className="no-statement-content">
                <ul>
                  <li>
                    Starting in late January, Mutual Fund tax forms will be made available
                    online as they are released.
                  </li>
                  <li>
                    For more information, view the{' '}
                    <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                      {' '}
                      Mutual Fund Tax Form Information Schedule
                    </a>
                    .
                  </li>
                </ul>
              </div>
              <div className="no-statement-content-section">
                <p>Why might a tax form not be available?</p>
                <ul>
                  <li>
                    A taxable or a non-retirement account (e.g., Individual or Joint Tenant)
                    had a distribution of income and/or captial gains of less than $10 during the year.
                  </li>
                  <li>
                    A retirement account (e.g., IRA) had no distributions or contributions during the year.
                  </li>
                  <li>
                    The Social Security Number (SSN) or Tax Identification Number (TIN) associated
                    with the account is different from that of the logged-in user.
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div>
                Starting in late January, Mutual Fund tax forms will be made available
                online as they are released. To see a list of all Mutual Fund tax
                forms and their expected release dates,{' '}
                <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                  {' '}
                  view the Mutual Fund Tax Form Information Schedule
                </a>
                .
              </div>
              <div className="no-statement-footer">
                Please note: Taxable accounts with income and capital gain
                distributions of less than $10 and retirement accounts, such as an
                individual retirement account (IRA), with no tax reportable activity
                during the year do not produce a required tax form.
              </div>
            </>
          )}
      </Fragment>
    );
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === BROKERAGE
  ) {
    return (
      <Fragment>
        <div className="no-statement-title">
          There are no Brokerage tax forms available for the selected time
          period.
        </div>
        {statementPageNoTaxDocumentEnabled() ?
          (
            <>
              <div className="no-statement-content">
                <ul>
                  <li>
                    Beginning January 31, required tax information on Brokerage accounts will be sent
                    by Pershing LLC.
                  </li>
                  <li>
                    All tax forms for taxable activity will be available by mid-March.
                  </li>
                  <li>
                    For more information, view the{' '}
                    <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                      {' '}
                      Tax Form Information Schedule
                    </a>
                    .
                  </li>
                </ul>
              </div>
              <div className="no-statement-content-section">
                <p>Why might a tax form not be available?</p>
                <ul>
                  <li>
                    A taxable account (e.g., Individual or Joint Tenant) had a distribution of
                    income and/or captial gains of less than $10 during the year.
                  </li>
                  <li>
                    A retirement account (e.g., IRA) had no distributions or contributions during the year.
                  </li>
                  <li>
                    The Social Security Number (SSN) or Tax Identification Number (TIN) associated
                    with the account is different from that of the logged-in user.
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div>
                Beginning January 31, required tax information on Brokerage accounts
                will be sent by Pershing LLC. All tax forms for taxable activity will
                be available by mid-March. For more information view the <TRP />
                Tax Information Mailing Schedule.{' '}
                <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                  View the Tax Information Mailing Schedule
                </a>
              </div>
              <div className="no-statement-footer">
                Please note: Taxable accounts with income and capital gain
                distribution of less than $10 and retirement accounts, such as an
                individual retirement account (IRA), with no tax reportable activity
                during the year do not produce a required tax form.
              </div>

            </>

          )}
      </Fragment>
    );
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === ACTIVE_PLUS
  ) {
    return (
      <Fragment>
        <div className="no-statement-title">
          There are no ActivePlus Portfolios® tax forms available for the
          selected time period.
        </div>
        {statementPageNoTaxDocumentEnabled() ?
          (
            <>
              <div className="no-statement-content">
                <ul>
                  <li>
                    Beginning January 31, required tax information on ActivePlus Portfolios
                    will be sent by Pershing LLC.
                  </li>
                  <li>
                    All tax forms for taxable activity will be available by mid-March.
                  </li>
                  <li>
                    For more information, view the{' '}
                    <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                      {' '}
                      Tax Form Information Schedule
                    </a>
                    .
                  </li>
                </ul>
              </div>
              <div className="no-statement-content-section">
                <p>Why might a tax form not be available?</p>
                <ul>
                  <li>
                    A retirement account (e.g., IRA) had no distributions or contributions during the year.
                  </li>
                  <li>
                    The Social Security Number (SSN) or Tax Identification Number (TIN) associated
                    with the account is different from that of the logged-in user.
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div>
                Beginning January 31, required tax information on ActivePlus
                Portfolios&reg; will be sent by Pershing LLC. All tax forms for
                taxable activity will be available by mid-March. For more information
                view the <TRP /> Tax Information Mailing Schedule.
                <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                  View the Tax Information Mailing Schedule
                </a>
              </div>
              <div className="no-statement-footer">
                Please note: Retirement Accounts, such as an individual retirement
                account (IRA), with no tax reportable activity during the year do not
                produce a required tax form.
              </div>

            </>
          )}
      </Fragment>
    );
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === ACTIVE_ADVISORY_SERVICE
  ) {
    return (
      <Fragment>
        <div className="no-statement-title">
          There are no Retirement Advisory Service tax forms available for the
          selected time period.
        </div>
        {statementPageNoTaxDocumentEnabled() ?
          (
            <>
              <div className="no-statement-content">
                <ul>
                  <li>
                    Beginning January 31, required tax information on Retirement Advisory Service
                    accounts will be sent by Pershing LLC.
                  </li>
                  <li>
                    All tax forms for taxable activity will be available by mid-March.
                  </li>
                  <li>
                    For more information, view the{' '}
                    <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                      {' '}
                      Tax Form Information Schedule
                    </a>
                    .
                  </li>
                </ul>
              </div>
              <div className="no-statement-content-section">
                <p>Why might a tax form not be available?</p>
                <ul>
                  <li>
                    A taxable account (e.g., Individual or Joint Tenant) had a distribution of
                    income and/or captial gains of less than $10 during the year.
                  </li>
                  <li>
                    A retirement account (e.g., IRA) had no distributions or contributions during the year.
                  </li>
                  <li>
                    The Social Security Number (SSN) or Tax Identification Number (TIN) associated
                    with the account is different from that of the logged-in user.
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>

              <div>
                Beginning January 31, required tax information on Retirement Advisory
                Service accounts will be sent by Pershing LLC. All tax forms for
                taxable activity will be available by mid-March. For more information
                view the <TRP /> Tax Information Mailing Schedule.
                <a href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
                  View the Tax Information Mailing Schedule
                </a>
              </div>
              <div className="no-statement-footer">
                Please note: Taxable accounts with income and capital gain
                distribution of less than $10 and retirement accounts, such as an
                individual retirement account (IRA), with no tax reportable activity
                during the year do not produce a required tax form.
              </div>
            </>
          )}
      </Fragment>
    );
  }

  if (
    documentTypes.selection === TAX_FORM_DOC_SELECTION &&
    timePeriod.selection === priorYear &&
    productTypes.selection === COLLEGE_SAVINGS
  ) {
    const collegeSavingsCopy = hasAlaska529Only
      ? '529 plan'
      : 'College Savings Plan';

    return (
      <Fragment>
        <div className="no-statement-title">
          There are no 529 {hasAlaska529Only ? 'plan' : 'College Savings Plans'}{' '}
          tax forms available for the selected time period.
        </div>
        <div>
          Starting in late January, Form 1099-Q will be issued for{' '}
          {collegeSavingsCopy} accounts, but only if a distribution was taken
          during the year. The form will not be available online unless the
          distribution was made payable to the account holder. Contributions
          into{' '}
          {hasAlaska529Only
            ? 'a 529 plan account'
            : 'College Savings Plan accounts'}{' '}
          will not generate a tax form.
        </div>
        <div className="no-statement-footer">
          To obtain information about contributions made to your{' '}
          {collegeSavingsCopy} accounts, you may refer to your year-end
          statement, which lists all contributions made during the year. Please
          note that other documentation of {collegeSavingsCopy} contributions
          for state tax purposes may be required; see your tax advisor for
          details. View the{' '}
          <a
            className="statements-here-button"
            href="https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html">
            Tax Form Schedule
          </a>{' '}
          for details when tax forms from <TRP /> will be available.
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      There are no documents available for the selected criteria.
    </Fragment>
  );
};
